import React, { useState, useEffect } from "react";
import { Select, DatePicker, Tooltip } from "antd";
import {
  RiSearchLine,
  RiDownload2Line,
  RiEyeLine,
  RiInboxLine,
} from "react-icons/ri";
import { toast } from "react-toastify";
import moment from "moment";
import Loader from "../../Components/Loader";

const { RangePicker } = DatePicker;

const WarrantyList = ({
  records = [],
  onViewDetails,
  loading,
  filters,
  onFilterChange,
  totalCount = 0,
  statuses = [],
}) => {
  const [searchTerm, setSearchTerm] = useState(filters?.search || "");
  const [dateRange, setDateRange] = useState(null);

  const pageSize = filters?.limit || 20;
  const currentPage = filters?.page || 1;
  const totalPages = Math.ceil((totalCount || records.length) / pageSize) || 1;

  // Sync current search when filters reset externally
  useEffect(() => {
    setSearchTerm(filters?.search || "");
  }, [filters?.search]);

  // Apply search explicitly when user presses Enter or clicks Search button
  const handleApplySearch = () => {
    onFilterChange({ search: searchTerm.trim(), page: 1 });
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    onFilterChange({ search: "", page: 1 });
  };

  const calculateFromTo = (dates) => {
    if (!dates || !dates[0] || !dates[1]) return { fromDate: "", toDate: "" };
    const fromDate = dates[0].format("YYYY-MM-DD");
    const toDate = dates[1].format("YYYY-MM-DD");
    return { fromDate, toDate };
  };

  const handleStatusChange = (val) => {
    onFilterChange({ status: val === "ALL" ? "" : val, page: 1 });
  };

  const handleDateChange = (dates) => {
    setDateRange(dates);
    onFilterChange({ ...calculateFromTo(dates), page: 1 });
  };

  const handlePageChange = (page) => {
    onFilterChange({ page });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const m = moment(dateStr);
    return m.isValid() ? m.format("DD/MM/YYYY") : dateStr;
  };

  const getStatusBadgeClass = (statusStr) => {
    const s = (statusStr || "").toLowerCase();
    if (s.includes("active") && !s.includes("yet")) return "active";
    if (s.includes("yet") || s.includes("activate")) return "yet-to-activate";
    if (s.includes("expire")) return "expired";
    return "active";
  };

  const getStatusLabel = (statusStr) => {
    if (!statusStr) return "Active";
    if (statusStr === "ACTIVE") return "Active";
    if (statusStr === "YET_TO_ACTIVATE") return "Yet to Activate";
    if (statusStr === "EXPIRED") return "Expired";
    return statusStr.replace(/_/g, " ");
  };

  // Export to CSV functionality
  const handleExport = () => {
    if (records.length === 0) {
      toast.warning("No records to export.");
      return;
    }

    const headers = [
      "Customer Name",
      "Phone Number",
      "Bill Number",
      "Purchase Date",
      "Bicycle Brand",
      "Bicycle Model",
      "Bicycle Type",
      "Gear System",
      "Warranty Start",
      "Warranty End",
      "Free Services Total",
      "Free Services Taken",
      "Free Services Remaining",
      "Status",
    ];

    const rows = records.map((item) => [
      `"${item.userid?.sfullname || item.customerName || item.name || ""}"`,
      `"${item.mobileNumber || item.userid?.imobilenumber || item.phone || item.mobile || ""}"`,
      `"${item.invoiceNumber || item.billNo || item.billNumber || ""}"`,
      `"${item.purchaseDate ? moment(item.purchaseDate).format("YYYY-MM-DD") : ""}"`,
      `"${item.bikeid?.brand?.brandName || item.bicycle?.brand || item.brand || ""}"`,
      `"${item.bikeid?.model || item.bicycle?.model || item.model || ""}"`,
      `"${item.bikeid?.bikeType?.configValue || item.bicycle?.bicycleType || item.bicycleType || ""}"`,
      `"${item.bikeid?.gearSystem?.configValue || item.bicycle?.gearType || ""}"`,
      `"${item.warrantyStartDate ? moment(item.warrantyStartDate).format("YYYY-MM-DD") : ""}"`,
      `"${item.warrantyExpiryDate ? moment(item.warrantyExpiryDate).format("YYYY-MM-DD") : ""}"`,
      item.totalService ?? item.freeServices?.total ?? 2,
      item.serviceCount ?? item.freeServices?.taken ?? 0,
      item.servicesRemaining ?? Math.max(0, (item.totalService ?? 2) - (item.serviceCount ?? 0)),
      `"${item.status || ""}"`,
    ]);

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    const today = moment().format("YYYY-MM-DD");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `Warranty_Customers_${today}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success(`Exported ${records.length} warranty records successfully!`);
  };

  return (
    <div className="warranty-list-view">
      {/* Header Section */}
      <div className="warranty-header">
        <div className="header-left">
          <h1>Warranty Customer List</h1>
          <p>Manage and track customer bicycle warranties and service schedules.</p>
        </div>
      </div>

      {/* Unified Filters Row: Search, Status, Date Range, Export (All in same line) */}
      <div className="warranty-filters-row">
        <div className="search-box">
          <RiSearchLine className="search-icon" />
          <input
            type="text"
            placeholder="Search by customer name, mobile no, bill no..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleApplySearch();
              }
            }}
          />
          {searchTerm && (
            <button
              type="button"
              className="clear-search-btn"
              onClick={handleClearSearch}
              title="Clear search"
            >
              ×
            </button>
          )}
          <button
            type="button"
            className="search-submit-btn"
            onClick={handleApplySearch}
          >
            Search
          </button>
        </div>

        <Select
          value={filters?.status || ""}
          onChange={handleStatusChange}
          className="status-select"
          options={[
            { value: "", label: "All Status" },
            ...statuses.map((s) => ({ value: s, label: getStatusLabel(s) })),
          ]}
        />

        <RangePicker
          className="date-picker-custom"
          placeholder={["From Date", "To Date"]}
          value={dateRange}
          onChange={handleDateChange}
          format="YYYY-MM-DD"
        />

        <button className="export-btn" onClick={handleExport}>
          <RiDownload2Line />
          Export
        </button>
      </div>

      {/* Table Card */}
      <div className="table-card">
        {records.length > 0 ? (
          <table className="warranty-table">
            <thead>
              <tr>
                <th>CUSTOMER NAME</th>
                <th>PHONE NO</th>
                <th>BILL NO</th>
                <th>BICYCLE BRAND</th>
                <th>WARRANTY PERIOD</th>
                <th>FREE SERVICES</th>
                <th>STATUS</th>
                <th style={{ textAlign: "center" }}>ACTION</th>
              </tr>
            </thead>
            <tbody>
              {records.map((item) => {
                const customerName =
                  item.userid?.sfullname ||
                  item.customerName ||
                  item.name ||
                  "-";
                const phone =
                  item.mobileNumber ||
                  item.userid?.imobilenumber ||
                  item.phone ||
                  item.mobile ||
                  "-";
                const billNo =
                  item.invoiceNumber ||
                  item.billNo ||
                  item.billNumber ||
                  "-";
                const purchaseDate =
                  item.purchaseDate || item.billDate || item.createdDate;
                const brand =
                  item.bikeid?.brand?.brandName ||
                  item.bicycle?.brand ||
                  item.brand ||
                  "-";
                const modelOrType =
                  item.bikeid?.model ||
                  item.bicycle?.model ||
                  item.bikeid?.bikeType?.configValue ||
                  item.bicycle?.bicycleType ||
                  item.bicycleType ||
                  "";
                const startDate =
                  item.warrantyStartDate || item.startDate || purchaseDate;
                const endDate =
                  item.warrantyExpiryDate || item.endDate;
                const totalServices =
                  item.totalService ?? item.freeServices?.total ?? 2;
                const takenServices =
                  item.serviceCount ?? item.freeServices?.taken ?? (item.serviceClaimedDate?.length || 0);
                const rawStatus = item.status || "ACTIVE";
                const effectiveStatus =
                  item.status === "YET_TO_ACTIVATE"
                    ? item.status
                    : endDate &&
                      moment(endDate).isValid() &&
                      moment(endDate).isBefore(moment(), "day")
                    ? "EXPIRED"
                    : rawStatus;

                return (
                  <tr key={item._id || item.id || billNo}>
                    <td>
                      <span className="primary-text">{customerName}</span>
                    </td>
                    <td>{phone}</td>
                    <td>
                      <span className="primary-text">{billNo}</span>
                      {purchaseDate && (
                        <span className="secondary-text">
                          {formatDate(purchaseDate)}
                        </span>
                      )}
                    </td>
                    <td>
                      <span className="primary-text">{brand}</span>
                      {modelOrType && (
                        <span className="secondary-text">{modelOrType}</span>
                      )}
                    </td>
                    <td>
                      {formatDate(startDate)} - {formatDate(endDate)}
                    </td>
                    <td>
                      <div className="free-services-badge">
                        <div className="dots">
                          {Array.from({ length: totalServices }).map((_, idx) => (
                            <span
                              key={idx}
                              className={`dot ${
                                idx >= takenServices ? "inactive" : ""
                              }`}
                            />
                          ))}
                        </div>
                        <span>
                          {takenServices}/{totalServices}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span
                        className={`status-badge ${getStatusBadgeClass(
                          effectiveStatus
                        )}`}
                      >
                        {getStatusLabel(effectiveStatus)}
                      </span>
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <Tooltip title="View Details">
                        <button
                          className="action-btn"
                          onClick={() => onViewDetails(item)}
                        >
                          <RiEyeLine />
                        </button>
                      </Tooltip>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="empty-state">
            {loading ? (
              <Loader />
            ) : (
              <>
                <RiInboxLine />
                <h3>No data</h3>
                <p>Try adjusting your search criteria or filter options.</p>
              </>
            )}
          </div>
        )}

        {/* Table Pagination Footer */}
        {records.length > 0 && (
          <div className="table-footer">
            <div className="entries-info">
              Showing {(currentPage - 1) * pageSize + 1} to{" "}
              {Math.min(currentPage * pageSize, totalCount || records.length)} of{" "}
              {totalCount || records.length} entries
            </div>

            <div className="pagination-controls">
              <button
                disabled={currentPage === 1}
                onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
              >
                Prev
              </button>

              {Array.from({ length: totalPages }).map((_, i) => {
                const pageNum = i + 1;
                if (
                  pageNum === 1 ||
                  pageNum === totalPages ||
                  (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={pageNum}
                      className={currentPage === pageNum ? "active-page" : ""}
                      onClick={() => handlePageChange(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                } else if (
                  pageNum === currentPage - 2 ||
                  pageNum === currentPage + 2
                ) {
                  return (
                    <span key={pageNum} style={{ padding: "0 4px" }}>
                      ...
                    </span>
                  );
                }
                return null;
              })}

              <button
                disabled={currentPage === totalPages}
                onClick={() =>
                  handlePageChange(Math.min(currentPage + 1, totalPages))
                }
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WarrantyList;
